// Core: api() Anthropic wrapper + JSON helpers + costanti modelli.
// PR-3a (ADR-0005): estrazione minima — solo helper puri, no state, no Supabase.
//
// State globals (S, PS, currentUser, _welcomeFlags) restano nello <script>
// inline di coach/index.html ed esposti su window in PR-3b quando builders.js
// li consumerà via window.*.
//
// Supabase client `sb` è ancora dichiarato nello <script> inline (CDN-based
// supabase-js v2 con flowType:'implicit'). Sarà spostato in core.js in PR-4
// (estrazione ui-auth.js) per evitare duplicate clients (ognuno con sua
// session = RLS rotti per query non autenticate). Per ora i moduli ESM che
// servono `sb` (builders.js in PR-3b) leggono via `window.sb`.

// Costanti modelli Anthropic (single source of truth, riusati in generators.js).
export const SONNET = 'claude-sonnet-5'; // era claude-sonnet-4-20250514 (ritirato da Anthropic 2026-06-15)
export const HAIKU = 'claude-haiku-4-5-20251001';

// Mappa slot composer ('sna') → slot DB ('sna1'|'sna2'|'sna3') dato l'indice
// nell'ambito della giornata. Centralizza mapping manuale in 3 punti del codice.
export function dbSlot(rawSlot, idx) {
  if (rawSlot !== 'sna') return rawSlot;
  return ['sna1', 'sna2', 'sna3'][idx] || 'sna3';
}

// ─── API (via Cloudflare Pages Function /api/anthropic) ────────────────────
// Wrapper async. Default model = HAIKU. Errori distinguono shape Anthropic
// (data.type==='error') vs function-level (data.error) vs unexpected (no content).
//
// opts.cache = true → invia `system` come array di blocchi con cache_control
// ephemeral, abilitando il prompt caching sul system prompt (vedi D-43).
// ATTENZIONE alla soglia: sotto il prefisso minimo cacheabile del modello
// (Haiku 4.5 = 4096 token, Sonnet 5 = 1024) l'API ignora il marker SENZA
// errore — `cache_creation_input_tokens` resta 0. Prima di attivarlo su una
// nuova chiamata, misura il system prompt: se sta sotto soglia il flag non
// rompe niente ma non serve a niente.

// Ultimo `usage` restituito dall'API. Serve a verificare che il caching stia
// davvero mordendo (cache_read_input_tokens > 0): senza questo il marker si
// puo mettere ma non si puo misurare. Sovrascritto a ogni chiamata.
let _lastUsage = null;
export function getLastUsage() { return _lastUsage; }

export async function api(msgs, sys, tok = 1000, model = HAIKU, opts = {}) {
  const body = { model: model, max_tokens: tok, messages: msgs };
  if (sys) {
    body.system = opts.cache
      ? [{ type: 'text', text: sys, cache_control: { type: 'ephemeral' } }]
      : sys;
  }
  const res = await fetch('/api/anthropic', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await res.json();
  _lastUsage = data.usage || null;
  if (data.type === 'error') throw new Error(data.error?.message || JSON.stringify(data));
  if (data.error) throw new Error(data.error.message || JSON.stringify(data.error));
  if (!data.content) throw new Error('Risposta inattesa: ' + JSON.stringify(data).slice(0, 200));
  return data.content.filter(b => b.type === 'text').map(b => b.text).join('');
}

// ─── JSON helpers ──────────────────────────────────────────────────────────
// Strip markdown code fences ```json ... ``` da response Haiku.
export const cj = raw => raw.trim().replace(/^```json\s*/, '').replace(/\s*```$/, '').replace(/^```\s*/, '').replace(/\s*```$/, '').trim();

// Ripara apostrofi non escaped dentro stringhe JSON.
// Sostituisce ' con uno spazio quando è dentro un valore stringa
// (pattern: char_non_escape ' char_non_separator).
export function fixJson(s) {
  return s.replace(/([^\\])'([^,:\[\]{}"\n])/g, "$1 $2");
}

// ─── parseLightMarkdown(src) ──────────────────────────────────────────────
// Parser markdown leggero per il body dell'announcement modal. Subset minimo:
// - **bold** → <strong>
// - *italic* → <em>
// - [label](url) → <a target="_blank" rel="noopener"> (solo https:// e mailto:)
// - "- item" su righe consecutive → <ul><li>...</li></ul>
// - righe non-vuote separate da blank line → <p>...</p>
// - tutto il resto → escape HTML (anti-XSS baseline).
//
// Limitation nota: `5*5` viene interpretato come italic (rendering "5*5" →
// <em>5</em>). Accettabile per il caso d'uso (annuncio di release), mitigato
// evitando di scrivere "*" letterali in body senza spazi attorno.
export function parseLightMarkdown(src) {
  if (typeof src !== 'string') return '';

  // 1. Escape HTML
  let s = src
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // 2. Link [label](url) — solo schemi safe. URL escaped per attribute.
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (m, label, url) => {
    if (!/^(https:\/\/|mailto:)/.test(url)) return label;
    const safeUrl = url.replace(/"/g, '%22').replace(/'/g, '%27');
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });

  // 3. Bold prima di italic (regex non-greedy)
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // 4. Block-level: liste e paragrafi
  const lines = s.split('\n');
  const out = [];
  let inList = false;
  let paragraph = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      out.push(`<p>${paragraph.join(' ')}</p>`);
      paragraph = [];
    }
  };
  const flushList = () => {
    if (inList) {
      out.push('</ul>');
      inList = false;
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }
    if (/^- /.test(trimmed)) {
      flushParagraph();
      if (!inList) {
        out.push('<ul>');
        inList = true;
      }
      out.push(`<li>${trimmed.slice(2)}</li>`);
    } else {
      flushList();
      paragraph.push(trimmed);
    }
  }
  flushParagraph();
  flushList();

  return out.join('\n');
}

// ─── ANALYTICS ─────────────────────────────────────────────────────────────
// BKL-014 (ADR-0014): fire-and-forget event tracking → user_events table.
// Pulisce localStorage (sb-/coachai_), sessionStorage (sb-) e cookies.
// Usato da confirmLogout, _forceLogoutToLogin, rejectLegalAndLogout.
export function clearAuthSession() {
  try {
    Object.keys(localStorage).filter(k => k.startsWith('sb-') || k.startsWith('coachai_')).forEach(k => localStorage.removeItem(k));
    Object.keys(sessionStorage).filter(k => k.startsWith('sb-')).forEach(k => sessionStorage.removeItem(k));
    document.cookie.split(';').forEach(c => {
      const name = c.trim().split('=')[0];
      if (name) document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Strict';
    });
  } catch (_) {}
}

// Silent fail: analytics mai rompe UX. Zero PII nel payload.
export async function logEvent(name, payload = {}) {
  try {
    const sb = window.sb;
    const uid = window.S?.profile?.id;
    if (!sb || !uid) return;
    await sb.from('user_events').insert({ user_id: uid, event_name: name, payload });
  } catch (e) {
    console.warn('logEvent failed:', e.message);
  }
}
